import { useEffect, useState } from "react";
import { COLORS } from "../styles/themeColors";
import type { Evidence } from "../api/types";
import { chainage, dateTime } from "../lib/format";
import { Dialog } from "./Feedback";

const imageUrl = (frame: string, thumb = false) => `/api/frames/${encodeURIComponent(frame)}/image${thumb ? "?thumb=1" : ""}`;
export { imageUrl };

type Box = [number, number, number, number];
const pct = (v: number, of: number) => `${(v / of) * 100}%`;

/** Thin box placed in % of the image; the finding's name is shown next to the image, not on it. */
function DetBox({ b, w, h, dashed }: { b: Box; w: number; h: number; dashed?: boolean }) {
  return (
    <div className={`det-box${dashed ? " dashed" : ""}`} aria-hidden="true"
         style={{ left: pct(b[0], w), top: pct(b[1], h), width: pct(b[2] - b[0], w), height: pct(b[3] - b[1], h),
                  ["--det" as string]: dashed ? COLORS.boxUnreviewed : COLORS.boxVerified }} />
  );
}

/** A 16:9 window onto the image around the box: 2.5x the box, at least 15% of the frame wide. Null when it
 * would magnify less than ~2.2x (wider than 45% of the frame), where it only repeats the frame. */
function closeUp(b: Box, w: number, h: number): Box | null {
  const cx = (b[0] + b[2]) / 2, cy = (b[1] + b[3]) / 2;
  let cw = Math.max((b[2] - b[0]) * 2.5, (b[3] - b[1]) * 2.5 * (16 / 9), w * 0.15);
  if (cw > w * 0.45) return null;
  let ch = cw * (9 / 16);
  if (ch > h) { ch = h; cw = ch * (16 / 9); }
  const x = Math.min(Math.max(cx - cw / 2, 0), w - cw), y = Math.min(Math.max(cy - ch / 2, 0), h - ch);
  return [x, y, x + cw, y + ch];
}

/** Privacy-blurred frame with the detection box drawn over it, a close-up of the box, and the neighbouring
 * frames along the road. Click the frame for full size; hold B to hide the box. */
export function EvidenceViewer({ evidence, label, dashed }: { evidence: Evidence | null | undefined; label: string; dashed?: boolean }) {
  const [shown, setShown] = useState<string | null>(evidence?.frame_uuid ?? null);
  const [full, setFull] = useState(false);
  const [hideBox, setHideBox] = useState(false);
  useEffect(() => setShown(evidence?.frame_uuid ?? null), [evidence?.frame_uuid]);
  useEffect(() => {
    const typing = (e: KeyboardEvent) => ["INPUT", "TEXTAREA", "SELECT"].includes((e.target as HTMLElement).tagName);
    const down = (e: KeyboardEvent) => { if (e.key === "b" && !typing(e) && !e.ctrlKey && !e.metaKey) setHideBox(true); };
    const up = (e: KeyboardEvent) => { if (e.key === "b") setHideBox(false); };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, []);

  if (!evidence) {
    return <div className="evidence-frame"><div className="evidence-missing">No image is linked to this finding.</div></div>;
  }
  if (evidence.variant === "none") {
    return <div className="evidence-frame"><div className="evidence-missing">This image is waiting for privacy blurring and can't be shown yet.</div></div>;
  }
  const frame = shown ?? evidence.frame_uuid;
  const isMain = frame === evidence.frame_uuid;
  const w = evidence.width ?? 1920;
  const h = evidence.height ?? 1080;
  const b = isMain && !hideBox ? evidence.bbox : null;
  const crop = isMain && evidence.bbox ? closeUp(evidence.bbox, w, h) : null;
  const cw = crop ? crop[2] - crop[0] : 0, ch = crop ? crop[3] - crop[1] : 0;

  return (
    <div className="evidence">
      <button type="button" className="evidence-frame evidence-open" onClick={() => setFull(true)} aria-label={`Open the image of ${label} full size`}>
        <img src={imageUrl(frame)} alt={`Road image for ${label}`} width={w} height={h} />
        {b ? <DetBox b={b} w={w} h={h} dashed={dashed} /> : null}
        <span className="evidence-tag">
          {evidence.variant === "raw" ? "Not yet blurred · engineers only" : isMain ? "Detection frame" : "Neighbouring frame"}
        </span>
      </button>
      {crop ? (
        <figure className="evidence-closeup">
          <div className="evidence-frame" style={{ aspectRatio: "16 / 9" }}>
            <img src={imageUrl(evidence.frame_uuid)} alt=""
                 style={{ position: "absolute", maxWidth: "none", left: pct(-crop[0], cw), top: pct(-crop[1], ch), width: pct(w, cw), height: pct(h, ch) }} />
            {b ? <DetBox b={[b[0] - crop[0], b[1] - crop[1], b[2] - crop[0], b[3] - crop[1]]} w={cw} h={ch} dashed={dashed} /> : null}
            <span className="evidence-tag">Close-up</span>
          </div>
        </figure>
      ) : null}
      {evidence.neighbours?.length ? (
        <div className="thumbs" role="list" aria-label="Frames along the road">
          {[...evidence.neighbours, { frame_uuid: evidence.frame_uuid, chainage_m: evidence.chainage_m ?? null }]
            .sort((a, b) => (a.chainage_m ?? 0) - (b.chainage_m ?? 0))
            .map((n) => (
              <button key={n.frame_uuid} role="listitem" className={`thumb${n.frame_uuid === shown ? " current" : ""}`}
                      onClick={() => setShown(n.frame_uuid)} aria-label={`Show frame at ${chainage(n.chainage_m)}`}>
                <img src={imageUrl(n.frame_uuid, true)} alt="" loading="lazy" />
                <small>{chainage(n.chainage_m)}</small>
              </button>
            ))}
        </div>
      ) : null}
      {evidence.captured_at ? <p className="muted" style={{ fontSize: 12 }}>Captured {dateTime(evidence.captured_at)}</p> : null}
      <Dialog open={full} onClose={() => setFull(false)} labelledBy="ev-full-title">
        <div className="dialog-body" style={{ width: "min(1600px, calc(100vw - 32px))" }}>
          <div className="row">
            <h2 className="dialog-title" id="ev-full-title">{label}</h2>
            <span className="muted">{isMain ? chainage(evidence.chainage_m) : "Neighbouring frame"}</span>
            <span className="spacer" />
            <button className="btn btn-ghost btn-sm" onClick={() => setFull(false)}>Close</button>
          </div>
          <div className="evidence-frame">
            <img src={imageUrl(frame)} alt={`Road image for ${label}, full size`} width={w} height={h} />
            {b ? <DetBox b={b} w={w} h={h} dashed={dashed} /> : null}
          </div>
          <p className="muted" style={{ fontSize: 12 }}>Hold B to hide the box. Esc closes.</p>
        </div>
      </Dialog>
    </div>
  );
}
